import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Rocket, Loader2, CheckCircle2, AlertTriangle, XCircle, Shield, ChevronDown, ChevronUp, Terminal, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import ProvisioningBadge from "@/components/websites/ProvisioningBadge";
import { useState } from "react";
import { assertPleskProvisionSucceeded, getPipelineLogs, type PipelineLog, type VerifyResult } from "@/lib/pleskProvision";
import { buildDeployPayload } from "@/lib/deployHelper";
import { cn } from "@/lib/utils";
import WordPressConnectionCard from "@/components/websites/WordPressConnectionCard";

const syncOptions = [
  { value: "1h", label: "Every hour" },
  { value: "6h", label: "Every 6 hours" },
  { value: "24h", label: "Every 24 hours" },
  { value: "manual", label: "Manual only" },
];

const strategyOptions = [
  { value: "manual", label: "Manual", desc: "Deploy only when you click Deploy" },
  { value: "auto", label: "Auto", desc: "Auto-deploy when articles are approved" },
];

type Props = { siteId: string; domain: string | null; imported?: boolean };

function PipelineLogItem({ log }: { log: PipelineLog }) {
  const icon = log.status === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
    : log.status === 'warn' ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
    : <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />;

  return (
    <div className="flex items-start gap-2 py-1.5">
      {icon}
      <div className="min-w-0">
        <span className="text-xs font-medium text-foreground">{log.step.replace(/_/g, ' ')}</span>
        <p className="text-[10px] text-muted-foreground truncate">{log.message}</p>
      </div>
    </div>
  );
}

function VerifyChecks({ checks }: { checks: VerifyResult['checks'] }) {
  return (
    <div className="space-y-1">
      {checks.map((check, i) => {
        const icon = check.status === 'pass' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
          : check.status === 'warn' ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          : <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />;

        return (
          <div key={i} className="flex items-start gap-2 py-1.5">
            {icon}
            <div className="min-w-0">
              <span className="text-xs font-medium text-foreground">{check.name.replace(/_/g, ' ')}</span>
              <p className="text-[10px] text-muted-foreground">{check.detail}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DeploymentTab({ siteId, domain, imported = false }: Props) {
  const queryClient = useQueryClient();
  const [deploying, setDeploying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [pipelineLogs, setPipelineLogs] = useState<PipelineLog[]>([]);
  const [showLogs, setShowLogs] = useState(false);

  const { data: site, isLoading } = useQuery({
    queryKey: ["website-deploy", siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("websites")
        .select("id, provisioning_status, deployment_strategy, sync_frequency, plan_id, domain, technical_config")
        .eq("id", siteId)
        .single();
      if (error) throw error;
      return data;
    },
    refetchInterval: (query) => {
      const s = query.state.data;
      return s?.provisioning_status === "deploying" ? 10_000 : false;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: { deployment_strategy?: string; sync_frequency?: string }) => {
      const { error } = await supabase.from("websites").update(updates).eq("id", siteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["website-deploy", siteId] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Settings updated");
    },
  });

  const handleDeploy = async () => {
    if (!domain) {
      toast.error("No domain configured — add a domain first");
      return;
    }
    setDeploying(true);
    setPipelineLogs([]);
    setVerifyResult(null);
    try {
      const payload = await buildDeployPayload(siteId, domain);
      const { data, error } = await supabase.functions.invoke("plesk-provision", {
        body: payload,
      });
      if (error) throw error;
      assertPleskProvisionSucceeded(data);

      const logs = getPipelineLogs(data);
      setPipelineLogs(logs);
      setShowLogs(true);

      const status = (data as any)?.status;
      if (status === 'fully_provisioned') {
        toast.success("Full deployment completed successfully");
      } else if (status === 'completed_with_warnings') {
        toast.success("Deployment completed with some warnings — check logs");
      } else {
        toast.warning("Deployment partially completed — review the pipeline log");
      }

      queryClient.invalidateQueries({ queryKey: ["website-deploy", siteId] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
    } catch (err: any) {
      toast.error(err.message || "Deployment failed");
    } finally {
      setDeploying(false);
    }
  };

  const handleVerify = async () => {
    if (!domain) {
      toast.error("No domain configured");
      return;
    }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("verify-installation", {
        body: { website_id: siteId, domain },
      });
      if (error) throw error;
      setVerifyResult(data as VerifyResult);

      if ((data as VerifyResult).overall === 'pass') {
        toast.success("All checks passed — site is live!");
      } else if ((data as VerifyResult).overall === 'partial') {
        toast.warning("Some checks need attention");
      } else {
        toast.error("Verification failed — check details below");
      }

      queryClient.invalidateQueries({ queryKey: ["website-deploy", siteId] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
    } catch (err: any) {
      toast.error(err.message || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const handleSyncAll = async () => {
    if (!domain) {
      toast.error("No domain configured");
      return;
    }
    setSyncing(true);
    try {
      const payload = await buildDeployPayload(siteId, domain);
      // Add a flag to tell plesk-provision to only sync config (skip domain/site creation)
      const { data, error } = await supabase.functions.invoke("plesk-provision", {
        body: { ...payload, sync_only: true },
      });
      if (error) throw error;
      assertPleskProvisionSucceeded(data);

      const logs = getPipelineLogs(data);
      setPipelineLogs(logs);
      setShowLogs(true);

      toast.success("All configuration synced to server");
      queryClient.invalidateQueries({ queryKey: ["website-deploy", siteId] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
    } catch (err: any) {
      toast.error(err.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-1">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  const isDeploying = site?.provisioning_status === "deploying";
  const techConfig = site?.technical_config as any;
  const savedLogs = techConfig?.pipeline_logs as PipelineLog[] | undefined;
  const displayLogs = pipelineLogs.length > 0 ? pipelineLogs : (savedLogs || []);

  return (
    <div className="space-y-6 p-1">
      {/* Site connection — required for publishing articles */}
      <WordPressConnectionCard
        siteId={siteId}
        domain={domain}
        technicalConfig={techConfig}
      />

      {/* Deploy action */}
      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-foreground">{imported ? "Re-sync Configuration" : "Deployment"}</h4>
            <p className="text-[10px] text-muted-foreground">
              {imported
                ? "This site was imported. Re-sync pushes dashboard settings to the live site — no destructive re-install."
                : "Full pipeline: Account → Site → Theme → Content"}
            </p>
          </div>
          <ProvisioningBadge status={site?.provisioning_status || "pending"} />
        </div>
        <div className="flex gap-2">
          <button
            onClick={imported ? handleSyncAll : handleDeploy}
            disabled={(imported ? syncing : deploying) || isDeploying}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {(imported ? syncing : (deploying || isDeploying)) ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : imported ? (
              <RefreshCw className="h-4 w-4" />
            ) : (
              <Rocket className="h-4 w-4" />
            )}
            {imported ? (syncing ? "Syncing…" : "Re-sync Configuration") : (isDeploying ? "Deploying…" : "Deploy Now")}
          </button>
          <button
            onClick={handleVerify}
            disabled={verifying || !domain}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            {verifying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Shield className="h-4 w-4" />
            )}
            Verify
          </button>
        </div>

        {/* Site admin link & credentials */}
        {techConfig?.wp_admin_url && (
          <div className="rounded-md border border-border bg-card/50 p-3 space-y-2">
            <a
              href={techConfig.wp_admin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-primary hover:underline font-medium"
            >
              Open Site Admin →
            </a>
            {techConfig.wp_admin_login && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                <span className="text-muted-foreground">Login</span>
                <span className="text-foreground font-mono">{techConfig.wp_admin_login}</span>
                {techConfig.wp_admin_password && (
                  <>
                    <span className="text-muted-foreground">Password</span>
                    <span className="text-foreground font-mono">{techConfig.wp_admin_password}</span>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pipeline Logs */}
      {displayLogs.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden">
          <button
            onClick={() => setShowLogs(!showLogs)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-[#1a1b26] hover:bg-[#1f2033] transition-colors"
          >
            <div className="flex items-center gap-2">
              <Terminal className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-xs font-mono font-medium text-emerald-400">Deployment Log ({displayLogs.length} steps)</span>
            </div>
            {showLogs ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
          </button>
          {showLogs && (
            <div className="bg-[#0f1017] px-4 py-3 font-mono text-[11px] leading-relaxed max-h-72 overflow-y-auto space-y-1">
              {displayLogs.map((log, i) => {
                const color = log.status === 'ok' ? 'text-emerald-400'
                  : log.status === 'warn' ? 'text-amber-400'
                  : 'text-red-400';
                const prefix = log.status === 'ok' ? '✓' : log.status === 'warn' ? '⚠' : '✗';
                const time = new Date(log.ts).toLocaleTimeString('en-US', { hour12: false });
                return (
                  <div key={i} className="flex gap-2">
                    <span className="text-muted-foreground shrink-0">[{time}]</span>
                    <span className={`${color} shrink-0`}>{prefix}</span>
                    <span className="text-foreground/80">
                      <span className="text-blue-400">{log.step.replace(/_/g, ' ')}</span>
                      {' — '}
                      {log.message}
                    </span>
                  </div>
                );
              })}
              {deploying && (
                <div className="flex gap-2 animate-pulse">
                  <span className="text-muted-foreground">[{new Date().toLocaleTimeString('en-US', { hour12: false })}]</span>
                  <span className="text-emerald-400">⟳</span>
                  <span className="text-foreground/60">Processing…</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Verification Result */}
      {verifyResult && (
        <div className={cn(
          "rounded-lg border p-4 space-y-3",
          verifyResult.overall === 'pass' ? "border-emerald-500/30 bg-emerald-500/5"
            : verifyResult.overall === 'partial' ? "border-amber-500/30 bg-amber-500/5"
            : "border-destructive/30 bg-destructive/5"
        )}>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">Verification Result</h4>
            <span className={cn(
              "text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full",
              verifyResult.overall === 'pass' ? "bg-emerald-500/20 text-emerald-600"
                : verifyResult.overall === 'partial' ? "bg-amber-500/20 text-amber-600"
                : "bg-destructive/20 text-destructive"
            )}>
              {verifyResult.summary}
            </span>
          </div>
          <VerifyChecks checks={verifyResult.checks} />
        </div>
      )}

      {/* Deployment strategy */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Deployment Strategy</h4>
        <div className="space-y-2">
          {strategyOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateMutation.mutate({ deployment_strategy: opt.value })}
              className={`w-full rounded-lg border p-3 text-left transition-all ${
                site?.deployment_strategy === opt.value
                  ? "border-primary ring-1 ring-primary/30"
                  : "border-border hover:border-primary/20"
              }`}
            >
              <p className="text-sm font-medium text-foreground">{opt.label}</p>
              <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Sync frequency */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Sync Frequency</h4>
        <div className="grid grid-cols-2 gap-2">
          {syncOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateMutation.mutate({ sync_frequency: opt.value })}
              className={`rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
                site?.sync_frequency === opt.value
                  ? "border-primary bg-primary/5 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sync All Configuration */}
      <div className="rounded-lg border border-border p-4 space-y-3">
        <div>
          <h4 className="text-sm font-medium text-foreground">Sync All Configuration</h4>
          <p className="text-[10px] text-muted-foreground">Push all dashboard settings (design, content, AI rules, monetization) to the live website</p>
        </div>
        <button
          onClick={handleSyncAll}
          disabled={syncing || !domain}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-primary bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
        >
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {syncing ? "Syncing…" : "Sync Configuration to Server"}
        </button>
      </div>
    </div>
  );
}
